import { AbstractControl, FormControl, FormGroup } from '@angular/forms';

type FormControlValue<T> = T extends FormControl<infer R> ? R : never;
type FormMapping<T extends Record<string, AbstractControl<any>>> = {
  [p in keyof T]: T[p];
};
type ValueMapping<T extends Record<string, AbstractControl<any>>> = {
  [p in keyof T]: FormControlValue<T[p]>;
};
type PatchValue<T> =
  T extends FormMapping<infer R>
    ? { [p in keyof R]: FormControlValue<R[p]> | null | undefined }
    : never;

function mapValue<T>(value: T): T | null {
  switch (typeof value) {
    case 'string':
      return value || null;
    default:
      return value;
  }
}

/**
 * Maps empty string to null for form values and returns the value of
 * the entire form. To map only the values which have been updated,
 * use `generatePatch`.
 */
export function mapFormStringsToNull<T extends Record<string, any>>(
  form: FormGroup<T>,
): ValueMapping<T> {
  return Object.entries(form.controls).reduce(
    (acc, [key, control]) => ({ ...acc, [key]: mapValue(control.value) }),
    {} as ValueMapping<T>,
  );
}

export function generatePatch<T extends Record<string, FormControl<any>>>(
  form: FormGroup<T>,
): PatchValue<T> {
  return Object.entries(form.controls).reduce(
    (acc, [key, control]) =>
      control.dirty ? { ...acc, [key]: mapValue(control.value) } : acc,
    {} as PatchValue<T>,
  );
}

export type PatchBody<T extends Record<string, any>> = {
  [p in keyof T]: T[p] | null | undefined;
};
